import { Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import type { FactoryDto } from "@audit-plan/shared";
import { Rating, fmtD, fmtN } from "../pills";
import { ResponsiveList, monoStyle } from "../ResponsiveList";

/** Stato VendorHub dello stabilimento: Qualified in verde, Disqualified in rosso, il resto neutro. */
export function FactoryStatusTag({ s }: { s: string }) {
  if (!s) return null;
  const color = /^qualified$/i.test(s) ? "success" : /disqualified/i.test(s) ? "error" : "default";
  return <Tag color={color} style={{ marginInlineEnd: 0 }}>{s}</Tag>;
}

export function FactoryName({ f }: { f: Pick<FactoryDto, "name" | "isSeat" | "nameLocal"> & { active?: boolean } }) {
  const { t } = useTranslation();
  return (
    <span>
      {f.name} {f.isSeat && <Tag color="blue" style={{ marginInlineStart: 4 }}>{t("f_seat")}</Tag>}
      {f.active === false && <Tag style={{ marginInlineStart: 4 }}>{t("act_inactive")}</Tag>}
      {f.nameLocal && f.nameLocal !== f.name ? <div style={{ fontSize: 12, opacity: 0.6 }}>{f.nameLocal}</div> : null}
    </span>
  );
}

export function FactoryTable(props: { factories: FactoryDto[]; loading?: boolean; onOpen: (f: FactoryDto) => void }) {
  const { factories, loading, onOpen } = props;
  const { t } = useTranslation();
  const cols: ColumnsType<FactoryDto> = [
    { title: t("sapCode"), dataIndex: "code", render: (c: string | null, f) => <span style={monoStyle}>{c ?? f.displayCode ?? "—"}</span> },
    { title: t("f_factory"), dataIndex: "name", render: (_: unknown, f) => <FactoryName f={f} /> },
    { title: t("country"), dataIndex: "country", render: (c: string, f) => [f.city, c].filter(Boolean).join(", ") || "—" },
    { title: t("f_tier"), dataIndex: "tier", align: "center" },
    { title: t("f_status"), dataIndex: "status", render: (s: string) => <FactoryStatusTag s={s} /> },
    { title: t("rating"), align: "right", render: (_: unknown, f) => (f.rating != null || f.ratingOverride ? <span><Rating score={f.rating} override={f.ratingOverride} /> <span className="num">{fmtN(f.rating, 1)}</span></span> : "—") },
    { title: t("audits"), dataIndex: "auditCount", align: "right", render: (n?: number) => n ?? 0 },
    { title: t("f_lastAudit"), dataIndex: "lastAuditDate", render: (d?: string | null) => fmtD(d) },
    { title: t("f_nextAudit"), dataIndex: "nextAuditDate", render: (d?: string | null) => fmtD(d) },
  ];
  return (
    <ResponsiveList<FactoryDto>
      rowKey="id"
      loading={loading}
      dataSource={factories}
      columns={cols}
      pageSize={20}
      onRowClick={onOpen}
      emptyText={t("noResults")}
      card={{
        code: (f) => f.code ?? f.displayCode,
        title: (f) => <FactoryName f={f} />,
        subtitle: (f) => [f.city, f.country].filter(Boolean).join(", "),
        tags: (f) => (
          <>
            <FactoryStatusTag s={f.status} />
            {(f.rating != null || f.ratingOverride) && <Rating score={f.rating} override={f.ratingOverride} />}
          </>
        ),
        fields: (f) => [
          [t("f_tier"), f.tier],
          [t("audits"), f.auditCount ?? 0],
          [t("f_lastAudit"), fmtD(f.lastAuditDate)],
          [t("f_nextAudit"), fmtD(f.nextAuditDate)],
        ],
      }}
    />
  );
}
